// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { TFunction } from 'i18next';
import { PENSION_PLAN_DISPLAY_ASSET_TYPE } from '@shared/data-access/utils/holding-asset-type';

export const MARKET_CLASS_ORDER = [
  'Money Market',
  'Fixed Income',
  'Equity',
  'Alternative Investments',
  'Other',
] as const;

export const createMarketClassMap = (t: TFunction) =>
  new Map([
    ['Money Market', t('glossary.money-market', { ns: 'glossary' })],
    ['Fixed Income', t('glossary.fixed-income', { ns: 'glossary' })],
    ['Equity', t('glossary.equity', { ns: 'glossary' })],
    [
      'Alternative Investments',
      t('glossary.alternative-investments', { ns: 'glossary' }),
    ],
    ['Other', t('glossary.other', { ns: 'glossary' })],
  ]);

export const getPositionsGroupRowLabel = ({
  name,
  viewMode,
  productFamilyMap,
  marketClassMap,
  t,
}: {
  readonly name: string;
  readonly viewMode: 'market' | 'product-family';
  readonly productFamilyMap: Map<string, string>;
  readonly marketClassMap: Map<string, string>;
  readonly t: TFunction;
}): string => {
  if (name === 'total') {
    return t('common.total', { ns: 'common' });
  }

  if (viewMode === 'product-family') {
    return productFamilyMap.get(name) ?? name;
  }

  return marketClassMap.get(name) ?? name;
};

export const PRODUCT_FAMILY_ORDER = [
  'stock',
  'etf',
  'fund',
  'mutual_fund',
  PENSION_PLAN_DISPLAY_ASSET_TYPE,
  'other',
] as const;

export const createProductFamilyMap = (t: TFunction) =>
  new Map([
    ['fund', t('glossary.fund', { ns: 'glossary' })],
    ['mutual_fund', t('glossary.mutual-fund', { ns: 'glossary' })],
    [
      PENSION_PLAN_DISPLAY_ASSET_TYPE,
      t('glossary.pension-plan', { ns: 'glossary' }),
    ],
    ['stock', t('glossary.stock', { ns: 'glossary' })],
    ['etf', t('glossary.etf', { ns: 'glossary' })],
    ['other', t('glossary.other', { ns: 'glossary' })],
  ]);

export const getProductFamilyOrder = (productFamily: string): number => {
  const index = PRODUCT_FAMILY_ORDER.findIndex(
    type => type.toLowerCase() === productFamily.toLowerCase(),
  );

  return index >= 0 ? index : PRODUCT_FAMILY_ORDER.length;
};
